import { useTranslation } from 'react-i18next'

import type { TenantBranding } from '@/shared/auth/session'
import { pickBrandImage } from '@/shared/lib/brand-image'
import { cn } from '@/shared/lib/utils'

interface SidebarBrandProps {
  /** Marca do tenant; sem ela (carregando) usa só o nome. */
  branding: TenantBranding | undefined
  name: string
  collapsed?: boolean
}

/**
 * Topo do menu lateral. O super-admin escolhe, por tenant, entre ícone com
 * título e subtítulo, só o ícone ou uma imagem completa no lugar.
 */
export function SidebarBrand({ branding, name, collapsed = false }: SidebarBrandProps) {
  const { t } = useTranslation()
  const display = branding?.display ?? 'text'
  const title = branding?.title ?? name
  const subtitle = branding?.subtitle ?? t('app.name')
  // O menu é escuro nos dois temas: usa sempre a versão para fundo escuro.
  const iconUrl = pickBrandImage(branding, 'icon', true)
  const logoUrl = pickBrandImage(branding, 'logo', true)

  const icon = iconUrl ? (
    <img src={iconUrl} alt="" className="size-8 shrink-0 rounded-md bg-transparent object-contain" />
  ) : (
    <span
      aria-hidden
      className="grid size-8 shrink-0 place-items-center rounded-md bg-transparent font-display text-lg font-bold text-primary"
    >
      {title.slice(0, 1).toUpperCase()}
    </span>
  )

  return (
    <div
      className={cn(
        'flex h-16 shrink-0 items-center justify-center gap-3 border-b border-sidebar-border object-center',
        collapsed || display === 'icon' ? 'justify-center px-2' : 'px-5',
      )}
    >
      {display === 'image' && logoUrl ? (
        collapsed ? (
          icon
        ) : (
          <img src={logoUrl} alt={title} className="max-h-10 max-w-full object-contain" />
        )
      ) : (
        <>
          {icon}
          {collapsed || display === 'icon' ? null : (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-display text-[19px] font-bold text-sidebar-accent-foreground">
                {title}
              </span>
              <span className="truncate text-[11px] font-semibold tracking-[0.08em] text-sidebar-foreground/70 uppercase">
                {subtitle}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
